import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import {
  ApiError,
  createWorker,
  getWorkers,
  type Worker,
  type WorkerPayload,
} from "../../trabalhadores/trabalhadoresApi";
import { getUsers, type UserAdmin } from "../../users/usersApi";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar trabalhadores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível criar o trabalhador.";
};

export function NovoTrabalhadorView() {
  const navigate = useNavigate();
  const [nome, setNome] = useState("");
  const [codigo, setCodigo] = useState("");
  const [funcao, setFuncao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [custoHora, setCustoHora] = useState("");
  const [identityUserId, setIdentityUserId] = useState("");
  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [supervisorId, setSupervisorId] = useState("");
  const [supervisors, setSupervisors] = useState<Worker[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const list = await getUsers();
        if (isMounted) setUsers(list);
      } catch {
        // Non-blocking: sem permissão de admin, o dropdown de contas fica vazio.
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const list = await getWorkers();
        if (isMounted) setSupervisors(list.filter((worker) => worker.isSupervisor));
      } catch {
        // Non-blocking: a lista de encarregados fica vazia.
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const supervisorOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      supervisors.map((supervisor) => ({
        value: String(supervisor.id),
        label: `${supervisor.name}${supervisor.code ? ` (${supervisor.code})` : ""}`,
        keywords: supervisor.code ?? undefined,
      })),
    [supervisors],
  );

  const userAccountOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      users.map((account) => ({
        value: account.userId,
        label: `${
          account.firstName || account.lastName
            ? `${account.firstName} ${account.lastName}`.trim()
            : account.email
        } (${account.email})`,
        keywords: account.email,
      })),
    [users],
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!nome.trim()) {
      setSubmitError("O nome é obrigatório.");
      return;
    }

    setIsSaving(true);

    const payload: WorkerPayload = {
      name: nome.trim(),
      code: codigo.trim() || null,
      role: funcao.trim() || null,
      isActive: ativo,
      hourlyCost: custoHora !== "" ? parseFloat(custoHora) : null,
      identityUserId: identityUserId || null,
      isSupervisor,
      supervisorId: isSupervisor || supervisorId === "" ? null : Number(supervisorId),
    };

    try {
      await createWorker(payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/trabalhadores", {
      state: { toast: "Trabalhador criado com sucesso" },
    });
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/trabalhadores")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-trabalhador-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Trabalhador"}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Trabalhador"
        subtitle="Regista um novo trabalhador e define os seus dados."
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form
            id="novo-trabalhador-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Trabalhador
                  </h3>

                  <div>
                    <label
                      htmlFor="nome"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Nome <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nome"
                      type="text"
                      placeholder="Insira o nome do trabalhador"
                      value={nome}
                      onChange={(event) => setNome(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="codigo"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Identificação Interna
                    </label>
                    <input
                      id="codigo"
                      type="text"
                      placeholder="Ex: TRB-001"
                      value={codigo}
                      onChange={(event) => setCodigo(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="funcao"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Função / Categoria
                    </label>
                    <input
                      id="funcao"
                      type="text"
                      placeholder="Ex: Pedreiro, Eletricista"
                      value={funcao}
                      onChange={(event) => setFuncao(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="ativo"
                      type="checkbox"
                      checked={ativo}
                      onChange={(event) => setAtivo(event.target.checked)}
                      className="h-4 w-4"
                    />
                    <label
                      htmlFor="ativo"
                      className="text-foreground"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Ativo
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="isSupervisor"
                      type="checkbox"
                      checked={isSupervisor}
                      onChange={(event) => setIsSupervisor(event.target.checked)}
                      className="h-4 w-4"
                    />
                    <label
                      htmlFor="isSupervisor"
                      className="text-foreground"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Encarregado de Obra
                    </label>
                  </div>

                  {!isSupervisor && (
                    <div>
                      <SearchableSelect<string>
                        id="supervisorId"
                        label="Encarregado de Obra responsável"
                        labelClassName="block text-foreground mb-2"
                        value={supervisorId === "" ? null : supervisorId}
                        onChange={(next) => setSupervisorId(next ?? "")}
                        options={supervisorOptions}
                        placeholder="— Sem encarregado —"
                        searchPlaceholder="Procurar encarregado…"
                      />
                      <p
                        className="text-secondary-text mt-2"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        O encarregado valida as horas deste trabalhador e pode lançá-las
                        por ele.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados Financeiros
                  </h3>

                  <div>
                    <label
                      htmlFor="custoHora"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Custo / Hora (€)
                    </label>
                    <input
                      id="custoHora"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={custoHora}
                      onChange={(event) => setCustoHora(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                    />
                  </div>

                  <div>
                    <SearchableSelect<string>
                      id="identityUserId"
                      label="Conta de utilizador (Folha de Horas)"
                      labelClassName="block text-foreground mb-2"
                      value={identityUserId === "" ? null : identityUserId}
                      onChange={(next) => setIdentityUserId(next ?? "")}
                      options={userAccountOptions}
                      placeholder="— Sem conta associada —"
                      searchPlaceholder="Procurar conta…"
                    />
                    <p
                      className="text-secondary-text mt-2"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Associe uma conta para que este trabalhador possa lançar as
                      próprias horas na Folha de Horas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
